import React, { useState, useEffect } from 'react';
import { GenericList } from '../../builders/list';
import { Header, Sidebar, Breadcrumb, Footer } from '../../components';
import Loading from '../../components/loading';
import ConfirmationModal from '../../components/confirmation-modal';
import { useParams } from 'react-router-dom';
import { useStoreProducts } from '../../hooks/useStoreProducts';
import './management-store-product-list.scss';

export default function ViewManagementStoreProductList({ consumer, entity, history }) {
  const { connectorUid } = useParams();
  const [elements, setElements] = useState([]);
  const [messages, setMessages] = useState({});
  const [deleting, setDeleting] = useState(false);
  const [config, setConfig] = useState({});
  const [preferences, setPreferences] = useState(null);
  
  // Estados para o modal de confirmação
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  
  // Usar o hook personalizado
  const {
    products,
    loading,
    error,
    connectorInfo,
    deleteProduct,
    deleteMultipleProducts,
    refresh
  } = useStoreProducts(connectorUid);

  async function configTable() {
    const res = await fetch('./preferences.json');
    const prefs = await res.json();
    setPreferences(prefs);

    const Configuration = {
      options: {
        hasCheckbox: false,
        hasEdit: true,
        hasDelete: true,
      },
      fields: [
        {
          identifier: 'name',
          field: 'Nome',
          type: 'text',
        },
        {
          identifier: 'unique_name',
          field: 'ID Único',
          type: 'text',
        },
        {
          identifier: 'amount',
          field: 'Valor',
          type: 'text',
        }
      ],
    };

    setConfig(Configuration);
  }

  // Atualizar elementos quando produtos mudarem
  useEffect(() => {
    if (products.length === 0 && !loading) {
      setMessages({ noContent: 'Nenhum produto cadastrado.' });
      setElements({
        "result": [],
        "current_page": 1,
        "page_count": 1,
        "page_size": 0,
        "row_count": 0,
      });
    } else if (products.length > 0) {
      // Processar produtos para o formato do GenericList - SIMPLIFICADO
      const processedProducts = products.map((product, index) => {
        return {
          uid: product.uid || `product-${index}`,
          name: product.name || 'Nome não disponível',
          unique_name: product.unique_name || 'ID não disponível',
          amount: product.amount || '0',
        };
      });

      const elementsData = {
        "result": processedProducts,
        "current_page": 1,
        "page_count": 1,
        "page_size": processedProducts.length,
        "row_count": processedProducts.length,
      };
      
      setElements(elementsData);
      
      // Limpar mensagens se houver produtos
      setMessages({});
    }
  }, [products, loading]);

  // Mostrar erro se houver
  useEffect(() => {
    if (error) {
      setMessages({ error });
    }
  }, [error]);

  useEffect(() => {
    configTable();

    // Definir a rota no breadcrumb
    consumer.defineRoute({ 
      name: "Produtos", 
      uri: `/management/store/connector/${connectorUid}/product/list`
    });
  }, [connectorUid, connectorInfo]);

  // Função para abrir modal de confirmação
  function confirmDelete(productUid) {
    const product = products.find(p => p.uid === productUid);
    setProductToDelete(product);
    setShowDeleteModal(true);
  }

  // Função para fechar modal
  function closeDeleteModal() {
    setShowDeleteModal(false);
    setProductToDelete(null);
  }

  // Função para confirmar exclusão
  async function confirmDeleteProduct() {
    if (!productToDelete) return;

    setDeleting(true);
    setShowDeleteModal(false);

    try {
      const result = await deleteProduct(productToDelete.uid);
      
      if (result.success) {
        setMessages({ success: result.message });
      } else {
        setMessages({ error: result.message });
      }
    } catch (error) {
      setMessages({ error: 'Erro ao excluir produto. Tente novamente.' });
    } finally {
      setDeleting(false);
      setProductToDelete(null);
    }
  }

  async function handleDelete(data) {
    // Se for um único produto, abrir modal de confirmação
    if (!Array.isArray(data)) {
      confirmDelete(data);
      return;
    }

    // Para múltiplos produtos (se implementado no futuro)
    setDeleting(true);
    try {
      let result;
      if (Array.isArray(data)) {
        result = await deleteMultipleProducts(data);
      } else {
        result = await deleteProduct(data);
      }
      
      if (result.success) {
        setMessages({ success: result.message });
      } else {
        setMessages({ error: result.message });
      }
    } catch (error) {
      console.error('Erro ao excluir produto:', error);
      setMessages({ error: 'Erro ao excluir produto.' });
    }

    setDeleting(false);
  }

  async function handleMultiDelete(uids) {
    // Esta função não é mais necessária pois o hook já gerencia isso
    return handleDelete(uids);
  }

  async function deleteItem(uid) {
    // Esta função não é mais necessária pois o hook já gerencia isso
    return handleDelete(uid);
  }

  function onUpdate(uid) {
    history.push(`/management/store/connector/${connectorUid}/product/${uid}/edit`);
  }

  function createProduct() {
    history.push(`/management/store/connector/${connectorUid}/product/new`);
  }

  if (preferences) {
    return <div className="rdp-admin-product-list">
      <Header consumer={consumer} />
      <Sidebar 
        consumer={consumer}
        defineGroup={consumer ? consumer.defineGroup : null}
        defineRoute={consumer ? consumer.defineRoute : null}
        groups={consumer ? consumer.groups : null}
      />
      <Breadcrumb 
        currentRoute={consumer ? consumer.currentRoute : null}
        newOption={() => createProduct()}
        customNewOptionTitle="Novo Produto"
      />
      
      <div id="rdp-admin-content-area" className="rdp-admin-content">
        {/* Mensagens de feedback */}
        {messages.success && (
          <div className="alert alert--success">
            <span>{messages.success}</span>
            <button 
              className="alert__close"
              onClick={() => setMessages({ ...messages, success: null })}
            >
              ×
            </button>
          </div>
        )}
        
        {messages.error && (
          <div className="alert alert--error">
            <span>{messages.error}</span>
            <button 
              className="alert__close"
              onClick={() => setMessages({ ...messages, error: null })}
            >
              ×
            </button>
          </div>
        )}

        {/* Tabela personalizada seguindo padrão GenericList */}
        <div className="rdp-admin-product-list__table-container">
          {elements.result && elements.result.length > 0 ? (
            <div className="rdp-admin-product-list__table-wrapper">
              <table className="rdp-admin-product-list__table">
                <thead>
                  <tr>
                    <th>Nome</th>
                    <th>ID Único</th>
                    <th>Valor</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {elements.result.map((product, index) => (
                    <tr key={product.uid || index}>
                      <td>{product.name}</td>
                      <td>{product.unique_name}</td>
                      <td>{product.amount}</td>
                      <td>
                        <div className="rdp-admin-product-list__actions">
                          <button 
                            onClick={() => onUpdate(product.uid)}
                            className="btn btn--success btn--sm"
                          >
                            Editar
                          </button>
                          <button 
                            onClick={() => handleDelete(product.uid)}
                            className="btn btn--danger btn--sm"
                          >
                            Excluir
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="rdp-admin-product-list__empty">
              <div className="icon">📦</div>
              <p>Nenhum produto encontrado</p>
            </div>
          )}
        </div>
        
        {deleting ? <Loading msg="Excluindo produto..." /> : null}
      </div>

      {/* Modal de confirmação para exclusão */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteProduct}
        title="Excluir Produto"
        message={
          productToDelete 
            ? `Tem certeza que deseja excluir o produto "${productToDelete.name}"? Esta ação não pode ser desfeita.`
            : "Tem certeza que deseja excluir este produto?"
        }
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  } else {
    return "";
  }
}
